import { useMemo, useState } from 'react';
import type { Citation } from '../types/api';
import { chipLabel, formatPageTitle } from '../utils/citation';
import { isKbUrl } from '../utils/support';
import styles from './ContextPanel.module.css';
import ExternalArrowIcon from './ExternalArrowIcon';

interface Props {
  citations: Citation[];
  onClose: () => void;
  activeUrl: string | null;
  onActiveUrlChange: (url: string | null) => void;
}

function docBase(url: string): string {
  return url.split('#')[0];
}

function titleFromUrl(url: string): string {
  const segment = url.split('#')[0].replace(/\/$/, '').split('/').pop() ?? '';
  return segment
    ? segment.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
    : 'Documentation';
}

export default function ContextPanel({ citations, onClose, activeUrl, onActiveUrlChange }: Props) {
  const [chipsOpen, setChipsOpen] = useState(true);
  const [chipsExpanded, setChipsExpanded] = useState(false);
  const [alsoReadOpen, setAlsoReadOpen] = useState(false);

  // Sources stored with the cited flag split into what the answer cites and what was
  // only read (#474). Older ones carry no flag and keep the single list.
  const split = useMemo(
    () => citations.length > 0 && citations.every((c) => typeof c.cited === 'boolean'),
    [citations],
  );
  const listed = useMemo(
    () => (split ? citations.filter((c) => c.cited) : citations),
    [citations, split],
  );
  const alsoRead = useMemo(
    () => (split ? citations.filter((c) => !c.cited) : []),
    [citations, split],
  );
  const docsCitations = useMemo(() => listed.filter((c) => c.source !== 'kb'), [listed]);
  const kbCitations = useMemo(() => listed.filter((c) => c.source === 'kb'), [listed]);

  const activePage = useMemo(
    () => (activeUrl ? (citations.find((p) => p.source_url === docBase(activeUrl)) ?? null) : null),
    [citations, activeUrl],
  );

  const handleChipClick = (page: Citation) => {
    if (page.source === 'kb' || isKbUrl(page.source_url)) {
      window.open(page.source_url, '_blank', 'noopener,noreferrer');
      return;
    }
    onActiveUrlChange(page.source_url);
  };

  return (
    <aside className={styles.panel}>
      <div className={styles.header}>
        {citations.length > 0 ? (
          <button
            type="button"
            className={styles.headerLeft}
            onClick={() => setChipsOpen((o) => !o)}
            aria-label="Toggle sources list"
          >
            <svg
              aria-hidden="true"
              className={styles.headerIcon}
              viewBox="0 0 20 20"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
            >
              <path d="M4 3h8l4 4v10a1 1 0 01-1 1H4a1 1 0 01-1-1V4a1 1 0 011-1z" />
              <polyline points="12 3 12 7 16 7" />
            </svg>
            <span className={styles.headerTitle}>{split ? 'Sources' : 'Retrieved Sources'}</span>
            <span className={styles.headerCount}>{listed.length || citations.length}</span>
            <svg
              aria-hidden="true"
              className={`${styles.chevron} ${chipsOpen ? styles.chevronOpen : ''}`}
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="4 6 8 10 12 6" />
            </svg>
          </button>
        ) : (
          <div className={styles.headerLeft}>
            <svg
              aria-hidden="true"
              className={styles.headerIcon}
              viewBox="0 0 20 20"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
            >
              <path d="M4 3h8l4 4v10a1 1 0 01-1 1H4a1 1 0 01-1-1V4a1 1 0 011-1z" />
              <polyline points="12 3 12 7 16 7" />
            </svg>
            <span className={styles.headerTitle}>Documentation</span>
          </div>
        )}
        <button
          type="button"
          className={styles.closeBtn}
          onClick={onClose}
          aria-label="Close sources panel"
        >
          <svg
            aria-hidden="true"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          >
            <line x1="3" y1="3" x2="13" y2="13" />
            <line x1="13" y1="3" x2="3" y2="13" />
          </svg>
        </button>
      </div>

      {citations.length > 0 && (
        <div className={`${styles.chipsCollapse} ${chipsOpen ? styles.chipsCollapseOpen : ''}`}>
          <div className={styles.chipsSection}>
            <button
              type="button"
              className={styles.chipsToggle}
              onClick={() => setChipsExpanded((e) => !e)}
            >
              {chipsExpanded ? (
                <>
                  <svg
                    aria-hidden="true"
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="4 10 8 6 12 10" />
                  </svg>
                  Collapse
                </>
              ) : (
                <>
                  <svg
                    aria-hidden="true"
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="4 6 8 10 12 6" />
                  </svg>
                  Show all
                </>
              )}
            </button>
            {docsCitations.length > 0 && (
              <>
                <div className={styles.sectionLabel}>
                  {split ? 'Cited in the answer' : 'Documentation'}
                </div>
                <div
                  className={`${styles.chipsRow} ${chipsExpanded ? styles.chipsRowExpanded : ''}`}
                >
                  {docsCitations.map((page) => {
                    const idx = citations.indexOf(page);
                    const isActive = !!activeUrl && page.source_url === docBase(activeUrl);
                    return (
                      <button
                        type="button"
                        key={page.file_path}
                        className={`${styles.chip} ${isActive ? styles.chipActive : ''}`}
                        onClick={() => handleChipClick(page)}
                        title={chipLabel(page)}
                      >
                        <span className={styles.chipNumber}>{idx + 1}</span>
                        <span className={styles.chipLabel}>{chipLabel(page)}</span>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
            {kbCitations.length > 0 && (
              <>
                <div className={styles.sectionLabel}>
                  {split ? 'Support articles cited' : 'Support articles'}
                </div>
                <div
                  className={`${styles.chipsRow} ${chipsExpanded ? styles.chipsRowExpanded : ''}`}
                >
                  {kbCitations.map((page) => {
                    const idx = citations.indexOf(page);
                    return (
                      <button
                        type="button"
                        key={page.file_path}
                        className={styles.chip}
                        onClick={() => handleChipClick(page)}
                        title={`Open ${chipLabel(page)} in a new tab`}
                      >
                        <span className={styles.chipNumber}>{idx + 1}</span>
                        <span className={styles.chipLabel}>{chipLabel(page)}</span>
                        <ExternalArrowIcon className={styles.chipExternal} />
                      </button>
                    );
                  })}
                </div>
              </>
            )}
            {alsoRead.length > 0 && (
              <>
                <button
                  type="button"
                  className={styles.alsoReadToggle}
                  aria-expanded={alsoReadOpen}
                  onClick={() => setAlsoReadOpen((o) => !o)}
                >
                  <svg
                    aria-hidden="true"
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className={`${styles.chevron} ${alsoReadOpen ? styles.chevronOpen : ''}`}
                  >
                    <polyline points="4 6 8 10 12 6" />
                  </svg>
                  Also read ({alsoRead.length})
                </button>
                {alsoReadOpen && (
                  <div className={`${styles.chipsRow} ${styles.chipsRowExpanded}`}>
                    {alsoRead.map((page) => {
                      const isActive = !!activeUrl && page.source_url === docBase(activeUrl);
                      return (
                        <button
                          type="button"
                          key={page.file_path}
                          className={`${styles.chip} ${styles.chipRead} ${isActive ? styles.chipActive : ''}`}
                          onClick={() => handleChipClick(page)}
                          title={`${chipLabel(page)} (read, not cited)`}
                        >
                          <span className={styles.chipLabel}>{chipLabel(page)}</span>
                          {page.source === 'kb' && (
                            <ExternalArrowIcon className={styles.chipExternal} />
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {activeUrl && (
        <div className={styles.toolbar}>
          <span className={styles.toolbarTitle}>
            {activePage ? formatPageTitle(activePage.file_path) : titleFromUrl(activeUrl)}
          </span>
          <a
            href={activeUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.externalLinkBtn}
            title="Open in new tab"
          >
            <svg
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <title>Open in new tab</title>
              <path d="M7 3H3a1 1 0 00-1 1v9a1 1 0 001 1h9a1 1 0 001-1V9" />
              <path d="M10 2h4v4" />
              <line x1="14" y1="2" x2="7" y2="9" />
            </svg>
          </a>
        </div>
      )}

      {!activeUrl && citations.length > 0 && (
        <div className={styles.emptyState}>
          <svg
            aria-hidden="true"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="9" y1="13" x2="15" y2="13" />
            <line x1="9" y1="17" x2="13" y2="17" />
          </svg>
          <p>
            Select a documentation source
            <br />
            above to view it here
          </p>
        </div>
      )}

      {activeUrl && (
        <iframe className={styles.inlineViewer} src={activeUrl} title="Documentation" />
      )}
    </aside>
  );
}
